import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea } from 'recharts';
import { FaSync, FaChartLine, FaExclamationTriangle, FaClock, FaServer, FaNetworkWired, FaMicrochip, FaMemory, FaFilter, FaWifi, FaHeartbeat, FaChevronDown, FaChevronUp, FaSearchPlus, FaGlobe } from 'react-icons/fa';
import sanitizeText from '@/utils/sanitizeText';
import useRouterMonitoringSignalR from '@/hooks/useRouterMonitoringSignalR';
import useChartZoom from '@/hooks/useChartZoom';
import TimeRangeSelector from './TimeRangeSelector';
import './MonitoringCharts.scss';
import * as Sentry from '@sentry/react';

const REALTIME_WINDOW_MS = 15 * 60 * 1000;

function pruneOldPoints(data, windowMs = REALTIME_WINDOW_MS) {
    const cutoff = Date.now() - windowMs;
    return data.filter(d => d._ts >= cutoff);
}

function formatBytes(bytes) {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

const CHART_COLORS = {
    primary: '#007bff',
    success: '#28a745',
    danger: '#dc3545',
    warning: '#ffc107',
    info: '#17a2b8',
    purple: '#6f42c1',
    teal: '#20c997',
    orange: '#fd7e14',
    gray: '#6c757d',
    inbound: '#17a2b8',
    outbound: '#fd7e14'
};

const METRIC_SECTIONS = {
    icmp: { label: 'ICMP / Availability', icon: <FaHeartbeat /> },
    publicIp: { label: 'Public IP ICMP', icon: <FaGlobe /> },
    system: { label: 'System Resources', icon: <FaMicrochip /> },
    interfaces: { label: 'Interface Traffic', icon: <FaNetworkWired /> }
};

/** Distinct colour palette for public IP lines. */
const PUBLIC_IP_COLORS = [
    '#007bff', '#28a745', '#dc3545', '#fd7e14', '#6f42c1',
    '#20c997', '#e83e8c', '#17a2b8', '#ffc107', '#6c757d'
];

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="custom-tooltip">
            <p className="tooltip-label">{label}</p>
            {payload.map((entry, i) => (
                <p key={i} style={{ color: entry.color }}>{entry.name}: {entry.value}</p>
            ))}
        </div>
    );
};

/**
 * MonitoringCharts Component
 * Professional monitoring dashboard with real-time SignalR updates,
 * stat cards, metric section filters, interface selector, and responsive chart grid.
 */
const MonitoringCharts = ({ routerId, apiFetch }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeRange, setTimeRange] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);

    const [secondsSinceUpdate, setSecondsSinceUpdate] = useState(0);

    useEffect(() => {
        if (!lastUpdate) return;
        setSecondsSinceUpdate(0);
        const interval = setInterval(() => {
            setSecondsSinceUpdate(prev => prev + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [lastUpdate]);

    // Keep a ref in sync with timeRange
    // without needing to be recreated (avoids stale closure issues).
    const timeRangeRef = useRef(timeRange);
    useEffect(() => {
        timeRangeRef.current = timeRange;
    }, [timeRange]);

    // Metric data
    const [availabilityData, setAvailabilityData] = useState([]);
    const [rttData, setRttData] = useState([]);
    const [packetLossData, setPacketLossData] = useState([]);
    const [cpuData, setCpuData] = useState([]);
    const [memoryData, setMemoryData] = useState([]);
    const [interfaceData, setInterfaceData] = useState({});
    /** Public IP ICMP data keyed by label (e.g. "Fibre IPs", "Wireless IPs"). */
    const [publicIpData, setPublicIpData] = useState({});
    const [latestValues, setLatestValues] = useState({});

    // Monitoring config status (to inform user about SNMP)
    const [monitoringStatus, setMonitoringStatus] = useState(null);

    // Filters
    const [visibleSections, setVisibleSections] = useState({ icmp: true, publicIp: true, system: true, interfaces: true });
    const [selectedInterface, setSelectedInterface] = useState('all');
    const [showAllInterfaces, setShowAllInterfaces] = useState(false);

    // Ref for the TimeRangeSelector so drag-to-zoom can programmatically
    // set a custom date range across all charts.
    const timeRangeSelectorRef = useRef(null);

    /**
     * Handles zoom completion from the useChartZoom hook.
     * Programmatically sets the TimeRangeSelector to the selected date range,
     * which in turn triggers a data reload for all charts.
     */
    const handleZoomComplete = useCallback((from, to) => {
        timeRangeSelectorRef.current?.setCustomRange(from, to);
    }, []);

    // Drag-to-zoom selection state and chart prop generator.
    // Spread getChartZoomProps(chartId, chartData) onto any Recharts chart
    // to enable click-and-drag zoom selection.
    const { zoomState, getChartZoomProps } = useChartZoom({ onZoomComplete: handleZoomComplete });

    // SignalR
    const { isConnected, subscribeToRouter } = useRouterMonitoringSignalR({
        routerId: parseInt(routerId),
        onMetricUpdate: handleRealtimeMetricUpdate,
        onAvailabilityChanged: handleAvailabilityChange,
        enabled: true
    });

    useEffect(() => {
        // Load monitoring status once on mount to determine SNMP state
        const loadStatus = async () => {
            try {
                const response = await apiFetch(`/api/RouterConfig/${routerId}/monitoring-status`);
                const data = await response.json();
                if (data.success) setMonitoringStatus(data.data);
            }
            catch (catchError) {
                Sentry.captureException(catchError);
                /* non-critical */
            }
        };
        loadStatus();
    }, [apiFetch, routerId]);

    // ===== Data Processing Functions =====
    const getTimestampFormat = useCallback((hours) => {
        if (hours <= 1) return { hour: '2-digit', minute: '2-digit', second: '2-digit' };
        if (hours <= 24) return { hour: '2-digit', minute: '2-digit' };
        if (hours <= 168) return { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return { month: 'short', day: 'numeric' };
    }, []);

    const processRawData = useCallback((metrics) => {
        const fmt = getTimestampFormat(timeRange?.hours || 24);
        const sorted = [...metrics].sort((a, b) => new Date(a.collectedAt) - new Date(b.collectedAt));

        const availability = [], rtt = [], pktLoss = [], cpu = [], memory = [];
        const ifData = {};
        const pubIpData = {};
        const latest = {};

        sorted.forEach(metric => {
            const ts = new Date(metric.collectedAt).getTime();
            const timestamp = new Date(metric.collectedAt).toLocaleString('en-US', fmt);

            if (metric.metricType === 'ICMP_PublicIP' && metric.interfaceName) {
                const label = metric.interfaceName;
                if (!pubIpData[label]) pubIpData[label] = [];
                pubIpData[label].push({
                    _ts: ts,
                    time: timestamp,
                    rtt: parseFloat(metric.roundTripTime) || 0,
                    up: metric.isAvailable !== false
                });
            } else if (metric.metricType === 'ICMP') {
                rtt.push({ _ts: ts, time: timestamp, avg: parseFloat(metric.roundTripTime) || 0, min: parseFloat(metric.roundTripTime) || 0, max: parseFloat(metric.roundTripTime) || 0 });
                availability.push({ _ts: ts, time: timestamp, uptime: metric.isAvailable ? 100 : 0, downtime: metric.isAvailable ? 0 : 100 });
                pktLoss.push({ _ts: ts, time: timestamp, loss: parseFloat(metric.packetLoss) || 0 });
                latest.rtt = parseFloat(metric.roundTripTime) || 0;
                latest.available = metric.isAvailable;
                latest.packetLoss = parseFloat(metric.packetLoss) || 0;
            } else if (metric.metricType === 'SNMP_System') {
                const cpuVal = parseFloat(metric.cpuUsage) || 0;
                const memVal = parseFloat(metric.memoryUsage) || 0;
                cpu.push({ _ts: ts, time: timestamp, avg: cpuVal, max: cpuVal });
                memory.push({ _ts: ts, time: timestamp, avg: memVal, max: memVal });
                latest.cpu = cpuVal;
                latest.memory = memVal;
                if (metric.uptime) latest.uptime = metric.uptime;
                if (metric.totalMemory) latest.totalMemory = metric.totalMemory;
                if (metric.freeMemory) latest.freeMemory = metric.freeMemory;
            } else if (metric.metricType === 'SNMP_Interface' && metric.interfaceName) {
                const ifName = metric.interfaceName;
                if (!ifData[ifName]) ifData[ifName] = [];
                ifData[ifName].push({ _ts: ts, time: timestamp, inbound: metric.inboundTraffic || 0, outbound: metric.outboundTraffic || 0 });
                if (!latest.interfaces) latest.interfaces = {};
                latest.interfaces[ifName] = { inbound: metric.inboundTraffic || 0, outbound: metric.outboundTraffic || 0 };
            }
        });

        setAvailabilityData(availability);
        setRttData(rtt);
        setPacketLossData(pktLoss);
        setCpuData(cpu);
        setMemoryData(memory);
        setInterfaceData(ifData);
        setPublicIpData(pubIpData);
        setLatestValues(latest);
    }, [timeRange, getTimestampFormat]);

    const processAggregatedData = useCallback((metrics) => {
        const fmt = getTimestampFormat(timeRange?.hours || 24);
        const availability = [], rtt = [], pktLoss = [], cpu = [], memory = [];
        const latest = {};

        metrics.forEach(metric => {
            const ts = new Date(metric.periodStart).getTime();
            const timestamp = new Date(metric.periodStart).toLocaleString('en-US', fmt);
            if (metric.metricType === 'ICMP') {
                availability.push({ _ts: ts, time: timestamp, uptime: parseFloat(metric.uptimePercentage)?.toFixed(1) || 0, downtime: (100 - (metric.uptimePercentage || 0)).toFixed(1) });
                rtt.push({ _ts: ts, time: timestamp, avg: parseFloat(metric.avgRoundTripTime)?.toFixed(1) || 0, min: parseFloat(metric.minRoundTripTime)?.toFixed(1) || 0, max: parseFloat(metric.maxRoundTripTime)?.toFixed(1) || 0 });
                pktLoss.push({ _ts: ts, time: timestamp, loss: parseFloat(metric.avgPacketLoss)?.toFixed(1) || 0 });
                latest.rtt = parseFloat(metric.avgRoundTripTime) || 0;
                latest.available = (metric.uptimePercentage || 0) > 50;
                latest.packetLoss = parseFloat(metric.avgPacketLoss) || 0;
            } else if (metric.metricType === 'SNMP_System') {
                cpu.push({ _ts: ts, time: timestamp, avg: parseFloat(metric.avgCpuUsage)?.toFixed(1) || 0, max: parseFloat(metric.maxCpuUsage)?.toFixed(1) || 0 });
                memory.push({ _ts: ts, time: timestamp, avg: parseFloat(metric.avgMemoryUsage)?.toFixed(1) || 0, max: parseFloat(metric.maxMemoryUsage)?.toFixed(1) || 0 });
                latest.cpu = parseFloat(metric.avgCpuUsage) || 0;
                latest.memory = parseFloat(metric.avgMemoryUsage) || 0;
            }
        });

        setAvailabilityData(availability);
        setRttData(rtt);
        setPacketLossData(pktLoss);
        setCpuData(cpu);
        setMemoryData(memory);
        // Interface data is loaded separately via loadInterfaceMetrics — do not clear it here.
        setLatestValues(prev => ({ ...prev, ...latest }));
    }, [timeRange, getTimestampFormat]);

    // ===== Data Loading =====
    const loadRawMetrics = useCallback(async (hours, signal) => {
        const response = await apiFetch(`/api/RouterConfig/${routerId}/monitoring-metrics?hours=${hours}`, { signal });
        if (!response.ok) throw new Error('Failed to load monitoring metrics');
        const data = await response.json();
        if (data.success && data.data) processRawData(data.data);
    }, [apiFetch, routerId, processRawData]);

    const loadAggregatedMetrics = useCallback(async (hours, aggregationType, signal) => {
        const response = await apiFetch(`/api/RouterConfig/${routerId}/monitoring-aggregated?aggregationType=${aggregationType}&hours=${hours}`, { signal });
        if (!response.ok) throw new Error('Failed to load monitoring metrics');
        const data = await response.json();
        if (data.success && data.data) processAggregatedData(data.data);
    }, [apiFetch, routerId, processAggregatedData]);

    const loadInterfaceMetrics = useCallback(async (hours, signal) => {
        try {
            const clampedHours = Math.min(Math.ceil(hours), 720);
            const response = await apiFetch(
                `/api/RouterConfig/${routerId}/monitoring-metrics?hours=${clampedHours}&metricType=SNMP_Interface`,
                { signal }
            );
            if (!response.ok) return;
            const data = await response.json();
            if (!data.success || !data.data?.length) return;

            const fmt = getTimestampFormat(hours);
            const sorted = [...data.data].sort((a, b) => new Date(a.collectedAt) - new Date(b.collectedAt));
            const ifData = {};
            const ifLatest = {};

            sorted.forEach(metric => {
                if (metric.metricType !== 'SNMP_Interface' || !metric.interfaceName) return;
                const ts = new Date(metric.collectedAt).getTime();
                const timestamp = new Date(metric.collectedAt).toLocaleString('en-US', fmt);
                const ifName = metric.interfaceName;
                if (!ifData[ifName]) ifData[ifName] = [];
                ifData[ifName].push({ _ts: ts, time: timestamp, inbound: metric.inboundTraffic || 0, outbound: metric.outboundTraffic || 0 });
                ifLatest[ifName] = { inbound: metric.inboundTraffic || 0, outbound: metric.outboundTraffic || 0 };
            });

            if (Object.keys(ifData).length > 0) {
                setInterfaceData(ifData);
                setLatestValues(prev => ({ ...prev, interfaces: ifLatest }));
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            // Interface data is supplementary — silently ignore errors so charts still render.
        }
    }, [apiFetch, routerId, getTimestampFormat]);

    /**
     * Loads ICMP_PublicIP metrics from InfluxDB for the given time window.
     * Each data point includes the public IP label as interfaceName,
     * availability (isAvailable), and round-trip time (roundTripTime).
     */
    const loadPublicIpMetrics = useCallback(async (hours, signal) => {
        try {
            const clampedHours = Math.min(Math.ceil(hours), 720);
            const response = await apiFetch(
                `/api/RouterConfig/${routerId}/monitoring-metrics?hours=${clampedHours}&metricType=ICMP_PublicIP`,
                { signal }
            );
            if (!response.ok) return;
            const data = await response.json();
            if (!data.success || !data.data?.length) return;

            const fmt = getTimestampFormat(hours);
            const sorted = [...data.data].sort((a, b) => new Date(a.collectedAt) - new Date(b.collectedAt));
            const pubIp = {};

            sorted.forEach(metric => {
                if (metric.metricType !== 'ICMP_PublicIP' || !metric.interfaceName) return;
                const ts = new Date(metric.collectedAt).getTime();
                const timestamp = new Date(metric.collectedAt).toLocaleString('en-US', fmt);
                const label = metric.interfaceName;
                if (!pubIp[label]) pubIp[label] = [];
                pubIp[label].push({
                    _ts: ts,
                    time: timestamp,
                    rtt: parseFloat(metric.roundTripTime) || 0,
                    up: metric.isAvailable !== false
                });
            });

            if (Object.keys(pubIp).length > 0) {
                setPublicIpData(pubIp);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            // Public IP data is supplementary — silently ignore errors.
        }
    }, [apiFetch, routerId, getTimestampFormat]);

    const loadMetrics = useCallback(async () => {
        if (!timeRange) return;
        setLoading(true);
        setError(null);
        // Clear supplementary data upfront so stale charts from a previous time range don't linger.
        setInterfaceData({});
        setPublicIpData({});
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000);
        try {
            const hours = timeRange.hours;
            if (hours <= 6) {
                // Raw metrics include all types (ICMP, SNMP_System, SNMP_Interface) in one request.
                await loadRawMetrics(Math.ceil(hours), controller.signal);
            } else {
                const aggregationType = hours <= 48 ? 'Hourly' : 'Daily';
                // Load aggregated ICMP/System data, raw interface data, and public IP data in parallel.
                await Promise.all([
                    loadAggregatedMetrics(Math.ceil(hours), aggregationType, controller.signal),
                    loadInterfaceMetrics(hours, controller.signal),
                    loadPublicIpMetrics(hours, controller.signal)
                ]);
            }
        } catch (err) {
            Sentry.captureException(err);
            if (err.name === 'AbortError') { setError('Request timed out'); }
            else { console.error('Error loading metrics:', err); setError(err.message || 'Failed to load metrics'); }
        } finally {
            clearTimeout(timeoutId);
            setLoading(false);
        }
    }, [timeRange, loadRawMetrics, loadAggregatedMetrics, loadInterfaceMetrics, loadPublicIpMetrics]);

    const handleTimeRangeChange = useCallback((range) => { setTimeRange(range); }, []);

    // ===== useEffect Hooks =====

    // Load metrics when timeRange or routerId changes
    useEffect(() => {
        if (timeRange) {
            (async () => { await loadMetrics(); })();
        }
    }, [routerId, timeRange, loadMetrics]);

    // Subscribe to SignalR when connected (but don't reload data)
    useEffect(() => {
        if (isConnected) {
            subscribeToRouter(parseInt(routerId));
        }
    }, [routerId, isConnected, subscribeToRouter]);

    // Only prune chart data when in realtime mode — historical data must never be pruned.
    useEffect(() => {
        if (!timeRange?.realtime) return;

        const tickInterval = setInterval(() => {
            setRttData(prev => pruneOldPoints(prev, REALTIME_WINDOW_MS));
            setPacketLossData(prev => pruneOldPoints(prev, REALTIME_WINDOW_MS));
            setCpuData(prev => pruneOldPoints(prev, REALTIME_WINDOW_MS));
            setMemoryData(prev => pruneOldPoints(prev, REALTIME_WINDOW_MS));
            setAvailabilityData(prev => pruneOldPoints(prev, REALTIME_WINDOW_MS));
            setInterfaceData(prev => {
                const pruned = {};
                Object.keys(prev).forEach(k => {
                    const prunedData = pruneOldPoints(prev[k], REALTIME_WINDOW_MS);
                    if (prunedData && prunedData.length > 0) {
                        pruned[k] = prunedData;
                    }
                });
                return pruned;
            });
            setPublicIpData(prev => {
                const pruned = {};
                Object.keys(prev).forEach(k => {
                    const prunedData = pruneOldPoints(prev[k], REALTIME_WINDOW_MS);
                    if (prunedData && prunedData.length > 0) {
                        pruned[k] = prunedData;
                    }
                });
                return pruned;
            });
        }, 5000);
        return () => clearInterval(tickInterval);
    }, [timeRange]);

    // ===== SignalR Handlers =====
    function handleRealtimeMetricUpdate(data) {
        setLastUpdate(new Date());

        // In historical mode, only refresh the stat cards — never touch the chart data.
        if (!timeRangeRef.current?.realtime) {
            if (data.metricType === 'ICMP') {
                setLatestValues(prev => ({
                    ...prev,
                    rtt: parseFloat(data.roundTripTime) || 0,
                    available: data.isAvailable !== false,
                    packetLoss: parseFloat(data.packetLoss) || 0
                }));
            } else if (data.metricType === 'SNMP_System') {
                setLatestValues(prev => ({
                    ...prev,
                    cpu: parseFloat(data.cpuUsage) || 0,
                    memory: parseFloat(data.memoryUsage) || 0,
                    ...(data.uptime ? { uptime: data.uptime } : {}),
                    ...(data.totalMemory ? { totalMemory: data.totalMemory } : {}),
                    ...(data.freeMemory ? { freeMemory: data.freeMemory } : {})
                }));
            }
            return;
        }

        // Realtime mode — append the incoming point to the rolling charts.
        // Use client time for _ts to avoid clock skew issues with pruning.
        const ts = Date.now();
        const displayTime = data.collectedAt ? new Date(data.collectedAt).toLocaleTimeString() : new Date(ts).toLocaleTimeString();
        const append = (prev, point) => [...prev, point];

        if (data.metricType === 'ICMP') {
            const rttVal = parseFloat(data.roundTripTime) || 0;
            const lossVal = parseFloat(data.packetLoss) || 0;
            setRttData(prev => append(prev, { _ts: ts, time: displayTime, avg: rttVal, min: rttVal, max: rttVal }));
            setAvailabilityData(prev => append(prev, { _ts: ts, time: displayTime, uptime: data.isAvailable !== false ? 100 : 0, downtime: data.isAvailable !== false ? 0 : 100 }));
            setPacketLossData(prev => append(prev, { _ts: ts, time: displayTime, loss: lossVal }));
            setLatestValues(prev => ({ ...prev, rtt: rttVal, available: data.isAvailable !== false, packetLoss: lossVal }));
        } else if (data.metricType === 'SNMP_System') {
            const cpuVal = parseFloat(data.cpuUsage) || 0;
            const memVal = parseFloat(data.memoryUsage) || 0;
            setCpuData(prev => append(prev, { _ts: ts, time: displayTime, avg: cpuVal, max: cpuVal }));
            setMemoryData(prev => append(prev, { _ts: ts, time: displayTime, avg: memVal, max: memVal }));
            setLatestValues(prev => ({
                ...prev, cpu: cpuVal, memory: memVal,
                ...(data.uptime ? { uptime: data.uptime } : {}),
                ...(data.totalMemory ? { totalMemory: data.totalMemory } : {}),
                ...(data.freeMemory ? { freeMemory: data.freeMemory } : {})
            }));
        } else if (data.metricType === 'ICMP_PublicIP' && data.interfaceName) {
            const label = data.interfaceName;
            const rttVal = parseFloat(data.roundTripTime) || 0;
            const point = { _ts: ts, time: displayTime, rtt: rttVal, up: data.isAvailable !== false };
            setPublicIpData(prev => {
                const existing = prev[label] || [];
                return { ...prev, [label]: [...existing, point] };
            });
        } else if (data.metricType === 'SNMP_Interface' && data.interfaceName) {
            const ifName = data.interfaceName;
            const inbound = parseFloat(data.inboundTraffic) || 0;
            const outbound = parseFloat(data.outboundTraffic) || 0;
            const point = { _ts: ts, time: displayTime, inbound, outbound };

            setInterfaceData(prev => {
                const existing = prev[ifName] || [];
                return { ...prev, [ifName]: [...existing, point] };
            });

            setLatestValues(prev => {
                const ifaces = { ...(prev.interfaces || {}), [ifName]: { inbound, outbound } };
                return { ...prev, interfaces: ifaces };
            });
        }
    }

    function handleAvailabilityChange(data) {
        setLastUpdate(new Date());
        setLatestValues(prev => ({ ...prev, available: data.isAvailable }));
    }

    const handleRefresh = () => { loadMetrics(); };

    const toggleSection = (section) => {
        setVisibleSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Interface list
    const interfaceNames = useMemo(() => {
        const names = Object.keys(interfaceData);
        if (latestValues.interfaces) {
            Object.keys(latestValues.interfaces).forEach(n => { if (!names.includes(n)) names.push(n); });
        }
        return names.sort();
    }, [interfaceData, latestValues.interfaces]);

    const MAX_VISIBLE_INTERFACES = 4;

    const displayedInterfaces = useMemo(() => {
        if (selectedInterface !== 'all') return interfaceNames.filter(n => n === selectedInterface);
        if (showAllInterfaces) return interfaceNames;
        return interfaceNames.slice(0, MAX_VISIBLE_INTERFACES);
    }, [interfaceNames, selectedInterface, showAllInterfaces]);

    // Data availability
    const hasAnyData = availabilityData.length > 0 || rttData.length > 0 || cpuData.length > 0 || memoryData.length > 0 || Object.keys(interfaceData).length > 0 || Object.keys(publicIpData).length > 0;
    const snmpEnabled = monitoringStatus?.snmpEnabled === true;
    const hasSnmpSystemData = cpuData.length > 0 || memoryData.length > 0;
    const hasSnmpInterfaceData = Object.keys(interfaceData).length > 0;
    const hasPublicIpData = Object.keys(publicIpData).length > 0;
    const publicIpLabels = useMemo(() => Object.keys(publicIpData).sort(), [publicIpData]);

    // Common chart props
    // angle: 0 keeps X-axis labels horizontal so they don't overlap the chart area.
    // height: 30 is enough for a single horizontal label line (was 60 for slanted labels).
    const xAxisProps = (dataLen) => ({
        dataKey: 'time',
        angle: 0,
        textAnchor: 'middle',
        height: 30,
        interval: Math.max(0, Math.floor(dataLen / 6)),
        tick: { fontSize: 11 }
    });

    // ===== Render =====
    return (
        <div className="monitoring-charts">
            {/* Header */}
            <div className="charts-header">
                <div className="header-left">
                    <h3><FaChartLine /> Live Monitoring</h3>
                    <div className="connection-status">
                        <div className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></div>
                        <span>{isConnected ? 'Live Updates' : 'Disconnected'}</span>
                        {lastUpdate && (
                            <span className="last-update">
                                <FaClock /> {secondsSinceUpdate}s ago
                            </span>
                        )}
                    </div>
                </div>
                <div className="header-right">
                    <TimeRangeSelector ref={timeRangeSelectorRef} onChange={handleTimeRangeChange} initialKey="realtime" />
                    <button onClick={handleRefresh} disabled={loading} className="refresh-btn" title="Refresh">
                        <FaSync className={loading ? 'spinning' : ''} />
                    </button>
                </div>
            </div>

            {/* Zoom hint — visible when charts have data */}
            {hasAnyData && (
                <div className="zoom-hint">
                    <FaSearchPlus /> Drag on any chart to zoom into a time range
                </div>
            )}

            {error && (
                <div className="error-message">
                    <FaExclamationTriangle /> {sanitizeText(error)}
                </div>
            )}

            {timeRange?.realtime && (
                <div className="realtime-indicator">
                    <div className="realtime-dot"></div>
                    <span>Realtime — rolling 15 minute window{isConnected ? '' : ' (waiting for connection)'}</span>
                </div>
            )}

            {/* Stat Cards */}
            <div className="stat-cards">
                <div className={`stat-card ${latestValues.available === false ? 'danger' : latestValues.available === true ? 'success' : ''}`}>
                    <div className="stat-icon"><FaHeartbeat /></div>
                    <div className="stat-content">
                        <div className="stat-value">{latestValues.available === true ? 'UP' : latestValues.available === false ? 'DOWN' : '-'}</div>
                        <div className="stat-label">Status</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon"><FaWifi /></div>
                    <div className="stat-content">
                        <div className="stat-value">{latestValues.rtt != null ? `${latestValues.rtt.toFixed(1)} ms` : '-'}</div>
                        <div className="stat-label">Latency</div>
                    </div>
                </div>
                <div className={`stat-card ${(latestValues.cpu || 0) > 80 ? 'warning' : ''}`} title={!snmpEnabled ? 'Enable SNMP monitoring to collect CPU data' : ''}>
                    <div className="stat-icon"><FaMicrochip /></div>
                    <div className="stat-content">
                        <div className="stat-value">{latestValues.cpu != null ? `${latestValues.cpu}%` : '-'}</div>
                        <div className="stat-label">CPU{!snmpEnabled && latestValues.cpu == null ? ' (SNMP)' : ''}</div>
                    </div>
                </div>
                <div className={`stat-card ${(latestValues.memory || 0) > 85 ? 'warning' : ''}`} title={!snmpEnabled ? 'Enable SNMP monitoring to collect memory data' : ''}>
                    <div className="stat-icon"><FaMemory /></div>
                    <div className="stat-content">
                        <div className="stat-value">{latestValues.memory != null ? `${latestValues.memory}%` : '-'}</div>
                        <div className="stat-label">Memory{!snmpEnabled && latestValues.memory == null ? ' (SNMP)' : ''}</div>
                    </div>
                </div>
                <div className="stat-card" title={!snmpEnabled ? 'Enable SNMP monitoring to collect RAM data' : ''}>
                    <div className="stat-icon"><FaServer /></div>
                    <div className="stat-content">
                        <div className="stat-value">{latestValues.totalMemory ? formatBytes(latestValues.totalMemory) : '-'}</div>
                        <div className="stat-label">Total RAM{!snmpEnabled && !latestValues.totalMemory ? ' (SNMP)' : ''}</div>
                    </div>
                </div>
            </div>

            {/* Section Filters */}
            <div className="section-filters">
                <span className="filter-label"><FaFilter /> Sections:</span>
                {Object.entries(METRIC_SECTIONS).map(([key, cfg]) => (
                    <button
                        key={key}
                        className={`filter-btn ${visibleSections[key] ? 'active' : ''}`}
                        onClick={() => toggleSection(key)}
                    >
                        {cfg.icon} {cfg.label}
                    </button>
                ))}
                {interfaceNames.length > 1 && visibleSections.interfaces && (
                    <select
                        className="interface-select"
                        value={selectedInterface}
                        onChange={(e) => setSelectedInterface(e.target.value)}
                    >
                        <option value="all">All Interfaces ({interfaceNames.length})</option>
                        {interfaceNames.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                )}
            </div>

            {/* Charts */}
            {loading && !hasAnyData ? (
                <div className="charts-grid">
                    <div className="no-data">
                        <div className="spinner"></div>
                        <p>Loading monitoring data...</p>
                    </div>
                </div>
            ) : (
                <div className="charts-grid">
                    {/* ── ICMP Section ── */}
                    {visibleSections.icmp && (
                        <>
                            {availabilityData.length > 0 && (
                                <div className="chart-container chart-zoomable">
                                    <h4><FaHeartbeat /> Availability</h4>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <BarChart data={availabilityData} margin={{ top: 5, right: 20, left: 0, bottom: 50 }} {...getChartZoomProps('availability', availabilityData)}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                            <XAxis {...xAxisProps(availabilityData.length)} />
                                            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Bar dataKey="uptime" stackId="a" fill={CHART_COLORS.success} name="Uptime %" isAnimationActive={false} />
                                            <Bar dataKey="downtime" stackId="a" fill={CHART_COLORS.danger} name="Downtime %" isAnimationActive={false} />
                                            {zoomState.selecting && zoomState.chartId === 'availability' && (
                                                <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} strokeOpacity={0.3} fill="#007bff" fillOpacity={0.15} />
                                            )}
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            {rttData.length > 0 && (
                                <div className="chart-container chart-zoomable">
                                    <h4><FaWifi /> Round Trip Time (Latency)</h4>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <LineChart data={rttData} margin={{ top: 5, right: 20, left: 0, bottom: 50 }} {...getChartZoomProps('rtt', rttData)}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                            <XAxis {...xAxisProps(rttData.length)} />
                                            <YAxis tickFormatter={v => `${v}ms`} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Line type="monotone" dataKey="avg" stroke={CHART_COLORS.primary} name="Avg RTT (ms)" dot={false} strokeWidth={2} isAnimationActive={false} />
                                            <Line type="monotone" dataKey="max" stroke={CHART_COLORS.warning} name="Max RTT (ms)" dot={false} strokeDasharray="5 5" isAnimationActive={false} />
                                            {zoomState.selecting && zoomState.chartId === 'rtt' && (
                                                <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} strokeOpacity={0.3} fill="#007bff" fillOpacity={0.15} />
                                            )}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            {packetLossData.length > 0 && (
                                <div className="chart-container chart-zoomable">
                                    <h4><FaExclamationTriangle /> Packet Loss</h4>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <AreaChart data={packetLossData} margin={{ top: 5, right: 20, left: 0, bottom: 50 }} {...getChartZoomProps('packetLoss', packetLossData)}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                            <XAxis {...xAxisProps(packetLossData.length)} />
                                            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Area type="monotone" dataKey="loss" stroke={CHART_COLORS.danger} fill={CHART_COLORS.danger} fillOpacity={0.3} name="Packet Loss %" isAnimationActive={false} />
                                            {zoomState.selecting && zoomState.chartId === 'packetLoss' && (
                                                <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} strokeOpacity={0.3} fill="#007bff" fillOpacity={0.15} />
                                            )}
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Public IP ICMP Section ── */}
                    {visibleSections.publicIp && (
                        <>
                            {hasPublicIpData && (
                                <PublicIpChart
                                    publicIpData={publicIpData}
                                    publicIpLabels={publicIpLabels}
                                    xAxisProps={xAxisProps}
                                    zoomState={zoomState}
                                    getChartZoomProps={getChartZoomProps}
                                />
                            )}
                            {!hasPublicIpData && (
                                <div className="chart-container">
                                    <div className="section-empty-state">
                                        <FaGlobe size={32} color="#ccc" />
                                        <p>No public IP monitoring data available</p>
                                        <span className="hint">
                                            Add public IP addresses with ICMP monitoring enabled in the Advanced → Interfaces &amp; Routing tab.
                                        </span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── System Section ── */}
                    {visibleSections.system && (
                        <>
                            {!hasSnmpSystemData && (
                                <div className="chart-container">
                                    <div className="section-empty-state">
                                        <FaMicrochip size={32} color="#ccc" />
                                        <p>{snmpEnabled ? 'Waiting for SNMP system data...' : 'SNMP monitoring is not enabled'}</p>
                                        <span className="hint">{snmpEnabled ? 'System metrics (CPU, memory, uptime) will appear once the first SNMP poll completes.' : 'Enable SNMP in the Configuration panel above to collect CPU, memory, uptime, and RAM data.'}</span>
                                    </div>
                                </div>
                            )}
                            {cpuData.length > 0 && (
                                <div className="chart-container chart-zoomable">
                                    <h4><FaMicrochip /> CPU Usage</h4>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <AreaChart data={cpuData} margin={{ top: 5, right: 20, left: 0, bottom: 50 }} {...getChartZoomProps('cpu', cpuData)}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                            <XAxis {...xAxisProps(cpuData.length)} />
                                            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Area type="monotone" dataKey="avg" stroke={CHART_COLORS.info} fill={CHART_COLORS.info} fillOpacity={0.4} name="CPU %" strokeWidth={2} isAnimationActive={false} />
                                            <Area type="monotone" dataKey="max" stroke={CHART_COLORS.danger} fill="none" strokeDasharray="4 4" name="Peak CPU %" isAnimationActive={false} />
                                            {zoomState.selecting && zoomState.chartId === 'cpu' && (
                                                <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} strokeOpacity={0.3} fill="#007bff" fillOpacity={0.15} />
                                            )}
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            {memoryData.length > 0 && (
                                <div className="chart-container chart-zoomable">
                                    <h4><FaMemory /> Memory Usage</h4>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <AreaChart data={memoryData} margin={{ top: 5, right: 20, left: 0, bottom: 50 }} {...getChartZoomProps('memory', memoryData)}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                            <XAxis {...xAxisProps(memoryData.length)} />
                                            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Area type="monotone" dataKey="avg" stroke={CHART_COLORS.purple} fill={CHART_COLORS.purple} fillOpacity={0.4} name="Memory %" strokeWidth={2} isAnimationActive={false} />
                                            <Area type="monotone" dataKey="max" stroke={CHART_COLORS.danger} fill="none" strokeDasharray="4 4" name="Peak Memory %" isAnimationActive={false} />
                                            {zoomState.selecting && zoomState.chartId === 'memory' && (
                                                <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} strokeOpacity={0.3} fill="#007bff" fillOpacity={0.15} />
                                            )}
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Interface Traffic Section ── */}
                    {visibleSections.interfaces && !hasSnmpInterfaceData && (
                        <div className="chart-container chart-full-width">
                            <div className="section-empty-state">
                                <FaNetworkWired size={32} color="#ccc" />
                                <p>{snmpEnabled ? 'Waiting for SNMP interface data...' : 'SNMP monitoring is not enabled'}</p>
                                <span className="hint">{snmpEnabled ? 'Per-interface traffic charts will appear once interface metrics are collected.' : 'Enable SNMP in the Configuration panel above to see per-interface traffic charts.'}</span>
                            </div>
                        </div>
                    )}
                    {visibleSections.interfaces && displayedInterfaces.map(ifName => {
                        if (!ifName || typeof ifName !== 'string') return null;
                        const ifPoints = interfaceData[ifName];
                        if (!ifPoints || !Array.isArray(ifPoints) || ifPoints.length === 0) return null;
                        const validPoints = ifPoints.filter(p => p && typeof p === 'object' && p._ts != null && p.time != null);
                        if (validPoints.length === 0) return null;
                        const chartLabel = /^\d+$/.test(ifName) ? `Interface ${ifName}` : ifName;
                        const ifChartId = `if_${ifName}`;
                        return (
                            <div key={ifName} className="chart-container chart-full-width chart-zoomable">
                                <h4><FaNetworkWired /> {chartLabel} — Traffic</h4>
                                <ResponsiveContainer width="100%" height={260}>
                                    <AreaChart data={validPoints} margin={{ top: 5, right: 20, left: 0, bottom: 50 }} {...getChartZoomProps(ifChartId, validPoints)}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                        <XAxis {...xAxisProps(validPoints.length)} />
                                        <YAxis tickFormatter={v => formatBytes(v)} />
                                        <Tooltip formatter={(val) => formatBytes(val)} />
                                        <Legend />
                                        <Area type="monotone" dataKey="inbound" stroke={CHART_COLORS.inbound} fill={CHART_COLORS.inbound} fillOpacity={0.3} name={`${chartLabel} In`} strokeWidth={2} isAnimationActive={false} />
                                        <Area type="monotone" dataKey="outbound" stroke={CHART_COLORS.outbound} fill={CHART_COLORS.outbound} fillOpacity={0.3} name={`${chartLabel} Out`} strokeWidth={2} isAnimationActive={false} />
                                        {zoomState.selecting && zoomState.chartId === ifChartId && (
                                            <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} strokeOpacity={0.3} fill="#007bff" fillOpacity={0.15} />
                                        )}
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        );
                    })}
                    {visibleSections.interfaces && selectedInterface === 'all' && interfaceNames.length > MAX_VISIBLE_INTERFACES && (
                        <div className="chart-container chart-full-width">
                            <button className="show-more-btn" onClick={() => setShowAllInterfaces(prev => !prev)}>
                                {showAllInterfaces ? <><FaChevronUp /> Show fewer interfaces</> : <><FaChevronDown /> Show all {interfaceNames.length} interfaces</>}
                            </button>
                        </div>
                    )}

                    {/* No Data */}
                    {!loading && !hasAnyData && (
                        <div className="no-data">
                            <FaChartLine />
                            <p>No monitoring data available for this time range.</p>
                            <p className="hint">Ensure ICMP and/or SNMP monitoring are enabled in the configuration panel above.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * PublicIpChart — renders a single multi-line chart with one RTT line per public IP.
 *
 * Merges per-IP time series into a unified dataset keyed by timestamp
 * so all IPs share the same X-axis. Down periods are stored as null RTT values,
 * which renders as gaps in the line chart rather than misleading zero-latency points.
 *
 * Each IP label gets a unique colour from PUBLIC_IP_COLORS.
 */
const PublicIpChart = ({ publicIpData, publicIpLabels, xAxisProps, zoomState, getChartZoomProps }) => {
    // Merge all per-IP data into a single array of { time, _ts, [label]: rtt, ... }.
    // Use the raw epoch timestamp as the merge key so points that share the same
    // display-formatted label (for example, multiple samples within one minute)
    // do not overwrite one another.
    const mergedData = useMemo(() => {
        // Collect all unique timestamps across all IPs using the internal timestamp
        // as the canonical key, while preserving the formatted display string for UI use.
        const timeMap = new Map();

        publicIpLabels.forEach(label => {
            const points = publicIpData[label] || [];
            points.forEach(p => {
                const key = p._ts;

                if (!timeMap.has(key)) {
                    timeMap.set(key, {
                        _ts: p._ts,
                        time: p.time
                    });
                }

                const entry = timeMap.get(key);
                entry[label] = p.up ? (p.rtt || 0) : null;
            });
        });

        // Sort by internal timestamp so the chart remains chronologically ordered.
        return Array.from(timeMap.values()).sort((a, b) => a._ts - b._ts);
    }, [publicIpData, publicIpLabels]);

    if (mergedData.length === 0) return null;

    return (
        <div className="chart-container chart-zoomable">
            <h4><FaGlobe /> Public IP Latency (ICMP)</h4>
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={mergedData} margin={{ top: 5, right: 20, left: 0, bottom: 50 }} {...getChartZoomProps('publicIpRtt', mergedData)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis {...xAxisProps(mergedData.length)} />
                    <YAxis tickFormatter={v => `${v}ms`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {publicIpLabels.map((label, idx) => (
                        <Line
                            key={label}
                            type="monotone"
                            dataKey={label}
                            stroke={PUBLIC_IP_COLORS[idx % PUBLIC_IP_COLORS.length]}
                            name={`${label} (ms)`}
                            dot={false}
                            strokeWidth={2}
                            connectNulls={true}
                            isAnimationActive={false}
                        />
                    ))}
                    {zoomState.selecting && zoomState.chartId === 'publicIpRtt' && (
                        <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} strokeOpacity={0.3} fill="#007bff" fillOpacity={0.15} />
                    )}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default MonitoringCharts;